import { makeAutoObservable, action, observable, runInAction } from "mobx";
import { getSpaceUsedInUnit } from "../util";
import { redirect } from "react-router-dom";
import http from '../http';
import * as Sentry from "@sentry/react";
class UserStore {
  username = null;
  phone = null;
  token = null;
  shouldRedirectToLogin = false;
  spaceLimit = 0;
  spaceUsed = 0;
  maxDimension = 0;
  subscriptionEndDate = null
  openWatermark = false
  watermark = null
  watermarkPosition = null
  watermarkFontSize = 0
  watermarkStyle = 0
  watermarkAlpha = 0
  watermarkShadow = false
  watermarkInset = 24
  watermarkRotate = 0
  userInfoLoaded = false
  globalTags = []
  globalTagsLoaded = false
  noticeUpgrade = false
  level = 0;
  packageImagesLimit = 0;
  unreadReplies = 0;
  unreadNotifications = 0;
  wechatDialogOpen = false;
  balance = 0;
  frozenBalance = 0;
  wxOpenId = null;

  constructor() {
    makeAutoObservable(this, {
      username: observable,
      phone: observable,
      token: observable,
      maxDimension:observable,
      level:observable,
      packageImagesLimit:observable,
      shouldRedirectToLogin: observable,
      subscriptionEndDate: observable,
      openWatermark:observable,
      watermark:observable,
      watermarkPosition:observable,
      watermarkFontSize:observable,
      watermarkStyle:observable,
      watermarkAlpha:observable,
      watermarkShadow:observable,
      watermarkInset:observable,
      watermarkRotate:observable,
      userInfoLoaded: observable,
      globalTags: observable,
      globalTagsLoaded: observable,
      spaceUsed: observable,
      spaceLimit: observable,
      extraSpace: observable,
      setShouldRedirectToLogin: action,
      login: action,
      logout: action,
      checkLogin: action,
      setUserInfo: action,
      addSpaceUsed: action,
      reduceSpaceUsed: action,
      noticeUpgrade:observable,
      setNoticeUpgrade:action,
      unreadReplies:observable,
      unreadNotifications: observable,
      wxOpenId: observable,
      wechatDialogOpen: observable,
      setWechatDialogOpen: action,
      setUnreadNotifications: action,
      setGlobalTags: action,
      balance: observable,
      frozenBalance: observable,
    });
  }

  get limit(){
    return this.level ==2 ? 500 : 300;
  }

  get subscriptionActive() {
    if (!this.subscriptionEndDate) return false;
    const expiresAt = new Date(this.subscriptionEndDate).getTime();
    return Number.isFinite(expiresAt) && Date.now() < expiresAt;
  }

  get canUseRaw() {
    return Number(this.level) >= 2 && this.subscriptionActive;
  }

  get spaceUsedInUnit() {
    return getSpaceUsedInUnit(this.spaceUsed)
  }

  get spaceLimitInUnit() {
    return getSpaceUsedInUnit(this.spaceLimit)
  }

  addExtraSpace(size) {
    this.spaceLimit += size;
    this.extraSpace += size;
  }

  addSpaceUsed(size) {
    this.spaceUsed += size;
  }
  reduceSpaceUsed(size) {
    this.spaceUsed -= size;
    if (this.spaceUsed < 0) {
      this.spaceUsed = 0;
    }
  }

  setUserInfo(userInfo) {
    let {
      username,
      phone,
      spaceUsed,
      spaceLimit,
      extraSpace,
      maxDimension,
      level,
      subscriptionEndDate,
      openWatermark,
      watermark,
      watermarkPosition,
      watermarkFontSize,
      watermarkStyle,
      watermarkAlpha,
      watermarkShadow,
      watermarkInset,
      watermarkRotate,
      noticeUpgrade,
      packageImagesLimit,
      unreadReplies,
      unreadNotifications,
      wxOpenId,
      balance,
      frozenBalance
    } = userInfo;
    this.username = username;
    this.phone = phone;
    this.spaceUsed = spaceUsed;
    this.spaceLimit = spaceLimit;
    this.extraSpace = extraSpace;
    this.maxDimension = maxDimension;
    this.level = level;
    this.balance = balance ?? 0;
    this.frozenBalance = frozenBalance ?? 0;
    this.subscriptionEndDate = subscriptionEndDate;
    this.openWatermark = openWatermark;
    this.watermark = watermark;
    this.watermarkPosition = watermarkPosition;
    this.watermarkFontSize = watermarkFontSize;
    this.watermarkStyle = watermarkStyle;
    this.watermarkAlpha = watermarkAlpha;
    this.watermarkShadow = watermarkShadow ?? false;
    this.watermarkInset = watermarkInset ?? 24;
    this.watermarkRotate = watermarkRotate ?? 0;
    this.userInfoLoaded = true;
    this.noticeUpgrade = noticeUpgrade;
    this.packageImagesLimit = packageImagesLimit;
    this.unreadReplies = unreadReplies
    this.unreadNotifications = unreadNotifications ?? this.unreadNotifications ?? 0;
    this.wxOpenId = wxOpenId;
    Sentry.setUser({ username });
  }

  refreshInfo(){
     http.get('/user/info').then((response) => {
        this.setUserInfo(response.data);
    }).catch(error => {});
  }

  resetUnreadReplies(){
    this.unreadReplies = 0;
  }

  setUnreadNotifications(value) {
    this.unreadNotifications = value ?? 0;
  }

  fetchUnreadNotifications = () => {
    return http.get('/user/notifications/unreadCount').then(res => {
      this.setUnreadNotifications(res.data?.unreadCount);
      return this.unreadNotifications;
    });
  }

  fetchNotifications = ({ page = 1, limit = 20, unreadOnly = false } = {}) => {
    const params = new URLSearchParams({ page, limit });
    if (unreadOnly) params.set('unreadOnly', 'true');
    return http.get(`/user/notifications?${params.toString()}`).then(res => {
      this.setUnreadNotifications(res.data?.unreadCount);
      return res.data;
    });
  }

  markNotificationRead = (id) => {
    return http.put(`/user/notifications/${id}/read`).then(res => {
      this.setUnreadNotifications(Math.max(0, (this.unreadNotifications || 0) - 1));
      return res.data;
    });
  }

  markAllNotificationsRead = () => {
    return http.put('/user/notifications/readAll').then(res => {
      this.setUnreadNotifications(0);
      return res.data;
    });
  }

  isWatermarkSet() {
    if( this.watermarkStyle == "single" ){
      return this.watermark?.length > 0 && this.watermarkPosition?.length > 0 && this.watermarkFontSize > 0  && this.watermarkAlpha > 0;
    }else{
      return this.watermark?.length > 0 && this.watermarkFontSize > 0  && this.watermarkAlpha > 0;
    }
  }

  setNoticeUpgrade(value){
    this.noticeUpgrade = value;
  }

  setWechatDialogOpen(value) {
    this.wechatDialogOpen = value;
  }

  setShouldRedirectToLogin(value) {
    this.shouldRedirectToLogin = value;
  }

  setWxOpenId(wxOpenId) {
    this.wxOpenId = wxOpenId;
  }

  fetchNotificationSettings = () => {
    return http.get('/user/notificationSettings').then(res => res.data);
  }

  updateNotificationSettings = (data) => {
    return http.put('/user/notificationSettings', data);
  }

  changePassword = (password) => {
    return http.post('/user/changePassword', { password });
  }

  changeUsername = (username) => {
    return http.post('/user/changeUsername', { username });
  }

  saveWatermarkSetting = (data) => {
    return http.post('/user/saveWaterMark', data).then(response => {
      runInAction(() => {
        if (data.openWatermark != null) this.openWatermark = data.openWatermark;
        if (data.watermark != null) this.watermark = data.watermark;
        if (data.position != null) this.watermarkPosition = data.position;
        if (data.fontSize != null) this.watermarkFontSize = data.fontSize;
        if (data.watermarkStyle != null) this.watermarkStyle = data.watermarkStyle;
        if (data.watermarkAlpha != null) this.watermarkAlpha = data.watermarkAlpha;
        if (data.watermarkShadow != null) this.watermarkShadow = data.watermarkShadow;
        if (data.watermarkInset != null) this.watermarkInset = data.watermarkInset;
        if (data.watermarkRotate != null) this.watermarkRotate = data.watermarkRotate;
      });
      return response;
    });
  }

  setGlobalTags(tags) {
    this.globalTags = (tags || []).filter(tag => tag.status !== 'archived');
    this.globalTagsLoaded = true;
  }

  fetchTags = ({ includeArchived = false } = {}) => {
    return http.get('/user/tags', {
      params: includeArchived ? { includeArchived: true } : undefined
    }).then(res => {
      this.setGlobalTags(res.data);
      return res.data;
    });
  }

  createTag = (data) => {
    return http.post('/user/tags', data).then(res => {
      const created = res.data;
      this.setGlobalTags([...this.globalTags.filter(tag => tag.id !== created.id), created]);
      return created;
    });
  }

  updateTag = (id, data) => {
    return http.put(`/user/tags/${id}`, data).then(res => {
      const updated = res.data;
      this.setGlobalTags(this.globalTags.map(tag => tag.id === updated.id ? updated : tag));
      return updated;
    });
  }

  deleteTag = (id) => {
    return http.delete(`/user/tags/${id}`).then(response => {
      this.setGlobalTags(this.globalTags.filter(tag => String(tag.id) !== String(id)));
      return response;
    });
  }

  restoreTag = (id) => {
    return http.post(`/user/tags/${id}/restore`).then(res => {
      const restored = res.data;
      this.setGlobalTags([...this.globalTags.filter(tag => tag.id !== restored.id), restored]);
      return restored;
    });
  }

  fetchMessages = () => {
    return http.get('/user/myMessages').then(res => res.data || []);
  }

  createMessage = (content) => {
    return http.post('/user/message/create', { content, replyType: 'messageBoard' });
  }

  fetchSubscriptions = (page) => {
    return http.get(`/user/subscriptions?page=${page}&limit=10`).then(res => res.data);
  }

  fetchWithdrawList = (page) => {
    return http.get(`/user/withdraw/list?page=${page}&limit=10`).then(res => {
      const data = res.data || {};
      return {
        ...data,
        withdrawals: Array.isArray(data.withdrawals) ? data.withdrawals.map(withdraw => ({
          ...withdraw,
          amount: Number(withdraw.amount ?? 0),
          actualAmount: Number(withdraw.actualAmount ?? 0),
          method: withdraw.method || 'WeChat',
        })) : [],
        totalWithdrawn: Number(data.totalWithdrawn ?? 0),
        pendingAmount: Number(data.pendingAmount ?? 0),
        totalPages: Number(data.totalPages ?? data.totalPage ?? 1),
      };
    });
  }

  cancelWithdraw = (id) => http.post(`/user/withdraw/${id}/cancel`).then(res => res.data);

  fetchInvoice = () => {
    return http.get('/user/invoice').then(res => res.data);
  }

  fetchInvoiceHistory = (page) => {
    return http.get(`/user/invoice/history?page=${page}&limit=10`).then(res => res.data);
  }

  fetchIncomes = (page) => {
    return http.get(`/user/incomes?page=${page}&limit=10`).then(res => {
      const data = res.data || {};
      const incomes = Array.isArray(data.incomes) ? data.incomes.map(income => ({
        ...income,
        price: Number(income.totalPrice ?? income.price ?? 0),
        paymentDate: income.paidTime ?? income.paymentDate ?? null,
        status: income.paymentStatus ?? income.status ?? 'pending',
      })) : [];

      return {
        ...data,
        incomes,
        totalIncome: Number(data.totalIncome ?? 0),
        totalPayments: Number(
          data.totalPaidSelections
          ?? data.totalPayments
          ?? data.totalPaidOrders
          ?? data.totalOrders
          ?? data.total
          ?? 0
        ),
        totalPages: Number(data.totalPages ?? data.totalPage ?? 1),
      };
    });
  }

  login(token) {
    if (this.token !== token) {
      this.userInfoLoaded = false;
      this.globalTags = [];
      this.globalTagsLoaded = false;
    }
    this.token = token;
    this.shouldRedirectToLogin = false;
    localStorage.setItem('token', token);
  }

  logout() {
    this.token = null;
    this.userInfoLoaded = false;
    this.globalTags = [];
    this.globalTagsLoaded = false;
    this.shouldRedirectToLogin = true;
    localStorage.removeItem('token');
    Sentry.setUser(null);
    redirect('/login');
  }

  checkLogin() {
    const token = localStorage.getItem('token');
    if (token) {
      this.token = token;
      return true;
    } else {
      this.shouldRedirectToLogin = true;
      return false;
    }
  }
}



//匿名用户 store
class AnonymousUserStore {
  accessToken = null;

  constructor() {
    makeAutoObservable(this);
    this.check();
  }

  save(accessToken) {
    this.accessToken = accessToken;
    localStorage.setItem('x-access-token', accessToken);
  }

  empty() {
    this.accessToken = null;
    localStorage.removeItem('x-access-token');
  }

  check() {
    const accessToken = localStorage.getItem('x-access-token');
    if (accessToken) {
      this.accessToken = accessToken;
      return true;
    }else{
      return false;
    }
  }
}

const userStore = new UserStore();
const anonymousUserStore = new AnonymousUserStore();

export {userStore,anonymousUserStore}
